import { useState, useCallback, useEffect, useRef } from 'react';
import {
  GOLDEN_RATIO,
  MIN_WIDTH_REM,
  MAX_WIDTH_VIEWPORT_RATIO,
  KEYBOARD_RESIZE_STEP_REM,
  REM_BASE,
  SESSION_STORAGE_KEY,
} from './AiChatDrawer.constants';

interface UseDrawerResizeResult {
  widthRem: number;
  isDragging: boolean;
  handleMouseDown: (e: React.MouseEvent) => void;
  handleKeyDown: (e: React.KeyboardEvent) => void;
  minWidthRem: number;
  maxWidthRem: number;
}

interface DragState {
  startX: number;
  startWidthRem: number;
  latestClientX: number;
}

const pxToRem = (px: number): number => px / REM_BASE;

const clampWidth = (width: number, min: number, max: number): number => Math.min(Math.max(width, min), max);

const computeViewportWidthRem = (): number => pxToRem(window.innerWidth);

const computeMinWidth = (): number => Math.min(MIN_WIDTH_REM, computeViewportWidthRem());

const computeGoldenWidth = (): number => pxToRem(window.innerWidth * GOLDEN_RATIO);

const computeMaxWidth = (): number =>
  Math.max(computeMinWidth(), pxToRem(window.innerWidth * MAX_WIDTH_VIEWPORT_RATIO));

const computeDefaultWidth = (): number => clampWidth(computeGoldenWidth(), computeMinWidth(), computeMaxWidth());

const readPersistedWidth = (): number | null => {
  try {
    const stored = sessionStorage.getItem(SESSION_STORAGE_KEY);
    if (stored === null) return null;
    const parsed = JSON.parse(stored);
    if (typeof parsed !== 'number' || !Number.isFinite(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
};

const getInitialWidth = (): number => {
  const persisted = readPersistedWidth();

  if (persisted !== null) {
    return clampWidth(persisted, computeMinWidth(), computeMaxWidth());
  }

  return computeDefaultWidth();
};

const persistWidth = (widthRem: number): void => {
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(widthRem));
  } catch {
    // sessionStorage may be full or unavailable
  }
};

const useDrawerResize = (isOpen: boolean): UseDrawerResizeResult => {
  const [widthRem, setWidthRem] = useState<number>(getInitialWidth);
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef<DragState | null>(null);
  const rafRef = useRef<number | undefined>(undefined);
  const removeDragListenersRef = useRef<VoidFunction>(() => undefined);
  const wasOpenRef = useRef(isOpen);

  const stopDragging = useCallback(() => {
    removeDragListenersRef.current();
    removeDragListenersRef.current = () => undefined;

    if (rafRef.current !== undefined) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = undefined;
    }

    dragStateRef.current = null;
    setIsDragging(false);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      setWidthRem(getInitialWidth());
    }

    if (!isOpen) {
      stopDragging();
    }

    wasOpenRef.current = isOpen;
  }, [isOpen, stopDragging]);

  useEffect(() => stopDragging, [stopDragging]);

  const syncWidthToViewport = useCallback(() => {
    setWidthRem(() => {
      const persistedWidth = readPersistedWidth();
      const baseWidth = persistedWidth ?? computeGoldenWidth();
      const clampedWidth = clampWidth(baseWidth, computeMinWidth(), computeMaxWidth());

      return clampedWidth;
    });
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    window.addEventListener('resize', syncWidthToViewport);

    return () => {
      window.removeEventListener('resize', syncWidthToViewport);
    };
  }, [isOpen, syncWidthToViewport]);

  const setClampedWidth = useCallback((nextWidth: number) => {
    const clampedWidth = clampWidth(nextWidth, computeMinWidth(), computeMaxWidth());

    setWidthRem(clampedWidth);
    persistWidth(clampedWidth);
  }, []);

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();

      stopDragging();

      dragStateRef.current = {
        startX: e.clientX,
        startWidthRem: widthRem,
        latestClientX: e.clientX,
      };

      const handleMouseMove = (nextEvent: MouseEvent) => {
        if (dragStateRef.current === null) {
          return;
        }

        dragStateRef.current.latestClientX = nextEvent.clientX;

        if (rafRef.current !== undefined) {
          return;
        }

        rafRef.current = requestAnimationFrame(() => {
          const currentDragState = dragStateRef.current;

          rafRef.current = undefined;

          if (currentDragState === null) {
            return;
          }

          const deltaRem = pxToRem(currentDragState.startX - currentDragState.latestClientX);
          setClampedWidth(currentDragState.startWidthRem + deltaRem);
        });
      };

      const handleMouseUp = () => {
        stopDragging();
      };

      removeDragListenersRef.current = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      setIsDragging(true);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [setClampedWidth, stopDragging, widthRem],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setClampedWidth(widthRem + KEYBOARD_RESIZE_STEP_REM);
      }
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        setClampedWidth(widthRem - KEYBOARD_RESIZE_STEP_REM);
      }
    },
    [setClampedWidth, widthRem],
  );

  const minWidthRem = computeMinWidth();
  const maxWidthRem = computeMaxWidth();

  return {
    widthRem,
    isDragging,
    handleMouseDown,
    handleKeyDown,
    minWidthRem,
    maxWidthRem,
  };
};

export default useDrawerResize;
